<template>
    <div class="modal-container modal-container--wizard">
        <div class="modal-header modal-header--title">
            <h2 class="modal-title">Bulk Upload Employees</h2>
            <button type="button" class="close-btn" :disabled="isImporting || isValidating" @click="closeDialog">
                <PhX :size="20" />
            </button>
        </div>

        <!-- Stepper -->
        <div class="stepper">
            <div
                v-for="(step, i) in steps"
                :key="step.id"
                class="stepper-item"
                :class="{ active: currentStep === i + 1, completed: currentStep > i + 1 }"
            >
                <div class="stepper-circle">
                    <PhCheck v-if="currentStep > i + 1" :size="16" weight="bold" />
                    <span v-else>{{ i + 1 }}</span>
                </div>
                <span class="stepper-label">{{ step.label }}</span>
                <div v-if="i < steps.length - 1" class="stepper-line" :class="{ completed: currentStep > i + 1 }" />
            </div>
        </div>

        <div class="modal-body modal-body--wizard">
            <!-- Step 1: Upload CSV -->
            <div v-show="currentStep === 1" class="step-content">
                <div class="upload-area">
                    <div class="drop-zone" :class="{ 'drag-over': isDragOver }" @dragover.prevent="handleDragOver" @dragleave="handleDragLeave" @drop.prevent="handleDrop">
                        <input type="file" ref="fileInput" accept=".csv" class="hidden" @change="handleFileSelect" />
                        <div v-if="!selectedFile" class="drop-zone-content" @click="openFilePicker">
                            <PhCloudArrowUp :size="40" class="text-zinc-400" />
                            <p>Drop your CSV here or <span class="browse-link">browse</span></p>
                        </div>
                        <div v-else class="selected-file">
                            <PhFile :size="24" class="text-green-500" />
                            <div>
                                <span class="file-name">{{ selectedFile.name }}</span>
                                <span class="file-size">{{ formatBytes(selectedFile.size) }}</span>
                            </div>
                            <button type="button" class="remove-file" @click.stop="removeFile">
                                <PhX :size="16" />
                            </button>
                        </div>
                    </div>
                    <p v-if="fileError" class="field-error" role="alert">{{ fileError }}</p>
                    <p class="template-link mt-4">
                        <PhFile :size="14" class="inline" />
                        Need a template? <a href="#" @click.prevent="downloadTemplate">Download CSV Template</a>
                    </p>
                    <!-- The limit and the license each row gets, stated before anything is sent (D13, D14). -->
                    <p id="upload-license-sentence" class="license-hint">
                        A file may hold at most {{ formatNumber(MAX_ROWS) }} employees. {{ licenseSentence }}
                    </p>
                    <div class="column-chips mt-6">
                        <p class="chip-label">Required columns: 3 cols</p>
                        <div class="chips">
                            <span class="chip">first_name</span>
                            <span class="chip">last_name</span>
                            <span class="chip">email</span>
                        </div>
                        <p class="chip-label mt-4">Optional columns: 9 cols</p>
                        <div class="chips">
                            <span class="chip">department</span>
                            <span class="chip">phone</span>
                            <span class="chip">dob</span>
                            <span class="chip">address_line_1</span>
                            <span class="chip">address_city</span>
                            <span class="chip">address_state</span>
                            <span class="chip">address_postal_code</span>
                            <span class="chip">address_country</span>
                            <span class="chip">role</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Step 2: Map Fields -->
            <div v-show="currentStep === 2" class="step-content">
                <p class="map-instruction mb-4">Match your CSV columns to the correct employee fields.</p>
                <div class="mapping-table">
                    <div class="mapping-header">
                        <span>Your CSV column</span>
                        <span>Maps to</span>
                        <span>Status</span>
                    </div>
                    <div v-for="(csvCol, idx) in csvHeaders" :key="idx" class="mapping-row">
                        <PrimeInputText :modelValue="csvCol" disabled class="csv-col-input" />
                        <PrimeSelect
                            :modelValue="columnMapping[csvCol]"
                            :options="fieldOptions"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="— Select field —"
                            class="map-select"
                            @update:modelValue="(v: string) => setMapping(csvCol, v)"
                        />
                        <span class="status-chip" :class="getMappingStatusClass(csvCol)">
                            <PhCheck v-if="columnMapping[csvCol]" :size="12" weight="bold" />
                            {{ getMappingStatusLabel(csvCol) }}
                        </span>
                    </div>
                </div>
                <!-- Next stays off until these are mapped; without this the only hint was a
                     chip per column, with nothing saying which required one was still missing. -->
                <p v-if="parsedCsv && missingRequired.length" class="field-error mapping-error" role="alert">
                    Match a column to {{ missingRequired.map((f) => FIELD_LABELS[f]).join(', ') }} to continue.
                </p>
            </div>

            <!-- Step 3: Preview & Review -->
            <div v-show="currentStep === 3" class="step-content">
                <div class="preview-summary mb-4">
                    <span class="summary-valid"><PhCheck :size="16" /> {{ formatNumber(validCount) }} ready to import</span>
                    <!-- Unchecked rows are counted apart from errors on purpose: they carry no
                         verdict, the import will still try them, and folding them in reported an
                         API outage as thousands of problems with the member's file. -->
                    <span v-if="validationErrorCount > 0" class="summary-errors"><PhWarning :size="16" /> {{ formatNumber(validationErrorCount) }} errors</span>
                    <span v-if="uncheckedCount > 0" class="summary-unchecked"><PhInfo :size="16" /> {{ formatNumber(uncheckedCount) }} we could not check</span>
                </div>
                <div class="preview-table-wrapper">
                    <table class="preview-table">
                        <thead>
                            <tr>
                                <th>Row</th>
                                <th>Status</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Dept</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="{ num, row } in previewRows"
                                :key="num"
                                :class="{ 'row-error': !row._valid }"
                            >
                                <td class="preview-row-num">{{ num }}</td>
                                <td>
                                    <span v-if="row._valid" class="status-chip valid">Valid</span>
                                    <span v-else class="status-chip error">▲ {{ row._error }}</span>
                                </td>
                                <td>{{ row.first_name ?? '—' }}</td>
                                <td>{{ row.last_name ?? '—' }}</td>
                                <td>{{ row.email ?? '—' }}</td>
                                <td>{{ row.role ?? '—' }}</td>
                                <td>{{ row.department ?? '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <!-- A file at this scale is not read row by row, so the table draws every
                     flagged row and a window of the rest; say what is not drawn rather
                     than letting the member count. -->
                <!-- A small file with nothing importable hides no rows, so the note below never
                     shows, and Import would sit disabled with no word on why. -->
                <p v-if="validatedRows.length > 0 && previewHidden === 0 && importableCount === 0" class="field-error import-blocked" role="alert">
                    No row here can be imported, so Import is off — fix the file and upload it again.
                </p>
                <p v-if="previewHidden > 0" class="preview-hidden">
                    Showing {{ formatNumber(previewRows.length) }} of {{ formatNumber(validatedRows.length) }} rows:<template v-if="flaggedRowsShown">
                    {{ formatNumber(flaggedRowsShown) }} with a problem</template><template v-if="flaggedRowsShown && validRowsShown"> and</template><template v-if="validRowsShown">
                    {{ formatNumber(validRowsShown) }} that are ready</template>. <template v-if="importableCount === 0">No row here can be imported, so Import is off — fix the file and upload it again.</template><template v-else>All {{ formatNumber(validatedRows.length) }} are sent, flagged rows included — the import checks every row again and answers for each<template v-if="uncheckedCount">, the {{ formatNumber(uncheckedCount) }} we could not check among them</template>.</template>
                </p>
                <!-- The table is bounded, so the rows it leaves out have to be reachable
                     somewhere: the same CSV the result screen offers, with each row's reason. -->
                <div v-if="flaggedHidden > 0" class="report-actions">
                    <button type="button" class="error-download-btn" @click="downloadFlaggedCsv">
                        <PhDownloadSimple :size="14" />
                        Download all {{ formatNumber(flaggedRows.length) }} flagged rows
                    </button>
                </div>
            </div>

            <!-- Step 4: Complete -->
            <div v-show="currentStep === 4" class="step-content complete-step">
                <div class="complete-icon">
                    <PhCheckCircle v-if="resultState === 'complete'" :size="48" weight="fill" class="text-green-500" />
                    <PhWarning v-else-if="resultState !== 'failed'" :size="48" weight="fill" class="text-amber-500" />
                    <PhXCircle v-else :size="48" weight="fill" class="text-red-500" />
                </div>
                <h3 class="complete-title">
                    {{ RESULT_TITLES[resultState] }}
                </h3>
                <p class="complete-message">
                    <template v-if="importResult?.imported && !settledAgainst">
                        {{ formatNumber(importResult.imported) }} employees added.
                        <!-- Only claim an email where the API counted one handed to the mail system (D21, D24). -->
                        <template v-if="importResult.emails_queued === undefined"> Invitation emails have been sent.</template>
                    </template>
                    <template v-else-if="importResult?.imported">
                        {{ formatNumber(importResult.imported) }} of {{ formatNumber((importResult.imported || 0) + settledAgainst) }} employees imported.<template v-if="rejectedCount">
                        {{ formatNumber(rejectedCount) }} rows had errors.</template><template v-if="stoppedCount"> The
                        import stopped before it could process {{ formatNumber(stoppedCount) }} of
                        them.</template>
                    </template>
                    <template v-else-if="importResult?.unconfirmed">
                        Nothing in this file could be confirmed as added.<template v-if="stoppedCount">
                        {{ formatNumber(stoppedCount) }} of its rows could not be processed, so those were not
                        added.</template><template v-if="rejectedCount"> {{ formatNumber(rejectedCount) }} had
                        errors.</template>
                    </template>
                    <template v-else>
                        No employees were imported.<template v-if="stoppedCount && rejectedCount">
                        {{ formatNumber(rejectedCount) }} rows had errors, and the import stopped before it could
                        process the other {{ formatNumber(stoppedCount) }}.</template><template v-else-if="stoppedCount"> The import
                        stopped before it finished, so none of these rows were added — nothing in your file was
                        rejected.</template><template v-else-if="importResult?.skipped"> All rows had
                        errors.</template>
                    </template>
                    <!-- Said in its own sentence, because it is a different fact from an error: the
                         request never answered, so whether these rows were created is unknown and
                         re-uploading them can duplicate people. -->
                    <template v-if="importResult?.unconfirmed">
                        {{ formatNumber(importResult.unconfirmed) }} rows could not be confirmed — check them before uploading again.
                    </template>
                </p>
                <div class="complete-summary">
                    <template v-if="importResult?.imported_with_license !== undefined">
                        <div class="summary-row">
                            <span>Added with a license</span>
                            <span class="text-green-600 font-semibold">{{ formatNumber(importResult.imported_with_license) }}</span>
                        </div>
                        <div class="summary-row">
                            <span>Added without a license<template v-if="unlicensedReason"> ({{ unlicensedReason }})</template></span>
                            <span class="text-amber-600 font-semibold">{{ formatNumber(importResult.imported_without_license ?? unlicensed.length) }}</span>
                        </div>
                    </template>
                    <div v-else class="summary-row">
                        <span>Employees added</span>
                        <span class="text-green-600 font-semibold">{{ formatNumber(importResult?.imported) }}</span>
                    </div>
                    <div class="summary-row">
                        <span>Skipped</span>
                        <span class="text-red-500 font-semibold">{{ formatNumber(importResult?.skipped) }}</span>
                    </div>
                </div>
                <p v-if="importResult?.imported && importResult.emails_queued !== undefined" class="complete-emails">
                    Welcome emails were queued for {{ importResult.emails_queued }} {{ importResult.emails_queued === 1 ? 'person' : 'people' }}.
                    <!-- Whose email failed is named below when the API says (D21); an API that
                         only counts leaves the admin to find them under Pending invitations. -->
                    <template v-if="emailsFailed.length">
                        {{ emailsFailed.length }} couldn't be emailed:
                    </template>
                    <template v-else-if="importResult.emails_queued < importResult.imported">
                        {{ importResult.imported - importResult.emails_queued }} couldn't be emailed; resend from Pending invitations.
                    </template>
                </p>
                <ul v-if="emailsFailed.length" class="emails-failed-list">
                    <li v-for="person in emailsFailed" :key="person.id">
                        <span>{{ person.name }}</span>
                        <span class="text-zinc-500">{{ person.email }}</span>
                        <span v-if="resend[person.id] === 'sent'" class="text-green-600">Sent</span>
                        <button v-else type="button" class="inline-link" :disabled="resend[person.id] === 'sending'" @click="resendInvite(person.id)">
                            {{ resend[person.id] === 'failed' ? "Couldn't send. Try again" : 'Resend' }}
                        </button>
                    </li>
                </ul>
                <div v-if="failedRows.length || unlicensed.length" class="report-actions">
                    <button type="button" class="error-download-btn" @click="downloadReportCsv">
                        <PhDownloadSimple :size="14" />
                        Download report
                    </button>
                </div>
                <div v-if="unlicensed.length" class="unlicensed-list">
                    <p class="error-list-title">
                        Added without a license:
                        <button v-if="seatsRanOut && canBuySeats" type="button" class="inline-link" @click="openPurchaseLicenses">Buy licenses</button>
                    </p>
                    <ul>
                        <li v-for="person in unlicensed" :key="person.row">
                            <span>{{ person.name }}</span>
                            <span class="text-zinc-500">{{ person.email }}</span>
                        </li>
                    </ul>
                </div>
                <div v-if="failedRows.length" class="error-list">
                    <div class="error-list-header">
                        <p class="error-list-title">{{ unconfirmedRows.length ? 'Rows to check:' : 'Skipped rows:' }}</p>
                        <span class="error-list-count">{{ errorPage * errorsPerPage < failedRows.length ? errorPage * errorsPerPage : failedRows.length }} of {{ failedRows.length }}</span>
                    </div>
                    <div class="error-list-items">
                        <div class="error-table-header">
                            <span>Row</span>
                            <span>Reason</span>
                        </div>
                        <div v-for="err in paginatedErrors" :key="err.row" class="error-item">
                            <span class="error-row">{{ err.row }}</span>
                            <span class="error-msg">
                                <span v-if="err.unconfirmed" class="status-chip unconfirmed">Unconfirmed</span>
                                {{ err.message }}
                            </span>
                        </div>
                    </div>
                    <div v-if="failedRows.length > errorsPerPage" class="error-pagination">
                        <button :disabled="errorPage <= 1" @click="errorPage--">&lsaquo; Prev</button>
                        <span>Page {{ errorPage }} of {{ totalErrorPages }}</span>
                        <button :disabled="errorPage >= totalErrorPages" @click="errorPage++">Next &rsaquo;</button>
                    </div>
                </div>
                <div v-if="importResult?.imported" class="info-alert">
                    <PhInfo :size="20" />
                    <span>Assign licenses from the Employees table or each employee's profile.</span>
                </div>
            </div>
            <!-- The file goes to the API a batch at a time, so say how far through it
                 is in rows (PB-050); a 10,000-row import otherwise looks hung. -->
            <p v-if="batchProgress" id="batch-progress" class="batch-progress" role="status">
                {{ isImporting ? 'Importing' : 'Checking' }} {{ formatNumber(batchProgress.done) }} of {{ formatNumber(batchProgress.total) }} rows…
            </p>
        </div>

        <div class="modal-footer modal-footer--wizard">
            <div class="footer-left">
                <PrimeButton v-if="currentStep < 4" label="Cancel" severity="secondary" :disabled="isImporting || isValidating" @click="closeDialog" />
            </div>
            <div class="footer-right">
                <!-- Back is disabled while an import runs, not only while validation does: a
                     multi-batch import takes minutes, Cancel and the close button are already
                     locked for it, and Back reached the mapping step where a fresh validation
                     replaced `validatedRows` under the run in flight — so the result list and
                     the downloaded report described a file the import had never been given. -->
                <PrimeButton
                    v-if="currentStep > 1 && currentStep < 4"
                    label="Back"
                    severity="secondary"
                    text
                    :disabled="isValidating || isImporting"
                    @click="goBack"
                >
                    <template #icon>
                        <PhCaretLeft :size="16" />
                    </template>
                </PrimeButton>
                <PrimeButton
                    v-if="currentStep === 1"
                    label="Next →"
                    :disabled="!parsedCsv"
                    @click="goToMapFields"
                />
                <PrimeButton
                    v-else-if="currentStep === 2"
                    label="Next →"
                    :loading="isValidating"
                    :disabled="!allRequiredMapped || isValidating"
                    @click="goToPreview"
                />
                <PrimeButton
                    v-else-if="currentStep === 3"
                    label="Import"
                    :loading="isImporting"
                    :disabled="importableCount === 0"
                    @click="runImport"
                />
                <PrimeButton
                    v-else-if="currentStep === 4"
                    label="Done"
                    @click="closeDialog"
                >
                    <template #icon>
                        <PhCheck :size="16" weight="bold" />
                    </template>
                </PrimeButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    PhX,
    PhXCircle,
    PhCheck,
    PhCheckCircle,
    PhCloudArrowUp,
    PhFile,
    PhWarning,
    PhInfo,
    PhCaretLeft,
    PhDownloadSimple,
} from '@phosphor-icons/vue';
import { ModalNames } from '~/types/modals';
import { formatBytes } from '@privacy-bee/format/bytes';
import { formatNumber } from '@privacy-bee/format/number';
import { businessService } from '~/services/business-service';
import { useBusinessStore } from '~/stores/business';
import type { BulkImportResult, License } from '~/types/business';
import { licenseTierName } from '~/types/licenses/tiers';
import { autoProvisionTier } from '~/utils/auto-provision';
import { checkoutBusinessUrl } from '~/utils/cross-app-urls';
import { isDemoMode } from '~/services/demo/mode';
import { ApiError, memberFacingMessage } from '~/utils/errors';

const REQUIRED_FIELDS = ['first_name', 'last_name', 'email'];
const OPTIONAL_FIELDS = ['department', 'phone', 'dob', 'address_line_1', 'address_city', 'address_state', 'address_postal_code', 'address_country', 'role'];
const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
// The API's ceiling per request (BulkUploadEmployeeController::MAX_ROWS). The
// server stays synchronous, so a file past it is split here and sent one batch at
// a time, in order (PB-050) — the endpoint keeps the shape and the load it already
// survives instead of growing a queue behind it.
const BATCH_SIZE = 100;
// The whole file's ceiling; a larger one is refused here, before anything is sent (D13).
const MAX_ROWS = 10_000;
/**
 * The byte ceiling, derived from the row ceiling rather than set beside it.
 *
 * A flat 5 MB against 10,000 rows is about 524 bytes a row, and a real export with the optional
 * address and role columns filled in goes past that easily — so the row capacity this branch raised
 * was unreachable for exactly the customers big enough to need it. Two kilobytes a row is generous
 * for the widest shape the template offers, and derived so raising one raises the other.
 */
const MAX_FILE_BYTES = MAX_ROWS * 2 * 1024;
// A batch that never answered: every row in it is named with this so the member can
// re-upload exactly those rows instead of guessing at the whole file.
const NOT_IMPORTED = "Couldn't be imported. Upload this row again.";
// A batch whose request never answered is NOT a batch the server refused. The connection may
// have dropped after it committed, and this POST carries no idempotency key, so telling the
// member to upload those rows again is how the same people get created twice.
const NOT_CONFIRMED = "Couldn't be confirmed — check whether this row was added before uploading it again.";
/**
 * A run the API stopped, told per row, because the errors table is where the member reads what
 * happened to each one. None of these is about the row, so none of them says "upload this row
 * again": the same sentence the row-level fallback gives would send a member hunting their file
 * for a problem that is in their session, the service's load, or the route.
 *
 * The validation pass already says the first two when IT stops ("The service is busy. Wait a
 * moment and check the file again." / "Your session may have ended."); this is the same guidance
 * where the import can put it, which is per row.
 */
const STOPPED_BUSY = 'Not imported — the service was busy. Wait a moment, then upload the remaining rows.';
const STOPPED_ACCESS = 'Not imported — your access to this company ended. Sign in again, then upload the remaining rows.';
const STOPPED_REFUSED = 'Not imported — the import was refused for the whole file, not for this row. Upload the remaining rows once it is available again.';
/**
 * The 4xx statuses that answer the ROWS. Every other one answers the REQUEST — the session, the
 * grant, the rate, the route, the size — and ninety-nine more identical requests cannot change
 * it, which is why anything else stops the run.
 *
 * Named this way round on purpose. Listing the run-level statuses instead left every status
 * nobody had thought of (404 for a company that is gone or a route that moved, 405, 413) resetting
 * the failure streak and continuing: a 10,000-row file made a hundred identical requests and then
 * reported every row as a content error.
 */
const ROW_LEVEL_STATUSES = [409, 422];

/**
 * Does this refusal answer the rows in front of it, or the request itself? Asked by the
 * validation pass and the import alike: a refusal the next batch cannot change has to stop both,
 * or the pass marks the remainder unchecked, shows a preview that invites an import, and the
 * import stops on the same refusal at its first request.
 */
const stopsTheRun = (e: unknown): boolean => e instanceof ApiError && e.code >= 400 && e.code < 500 && !ROW_LEVEL_STATUSES.includes(e.code);
// How many valid rows the preview table draws. Ten thousand rows of six cells is a
// DOM the browser spends seconds on and the member scrolls straight past; the rows
// the API flagged are what they are here to fix, and every one of those is shown.
const PREVIEW_VALID_SHOWN = 50;
const RESULT_TITLES = {
    complete: 'Import Complete',
    partial: 'Partial Import',
    unconfirmed: 'Import Outcome Unknown',
    failed: 'Import Failed',
} as const;
// And how many flagged ones. Normally every flagged row is drawn, because those are what
// the member came to fix — but when the API goes down mid-pass the remainder of the file
// is marked unchecked, so a 10,000-row file produced 9,800 table rows precisely when the
// browser had least to gain from them. Bounded the same way, with the full list a
// download away.
const PREVIEW_FLAGGED_SHOWN = 50;

const FIELD_LABELS: Record<string, string> = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email',
    department: 'Department',
    phone: 'Phone',
    dob: 'Date of Birth',
    address_line_1: 'Address Line 1',
    address_city: 'City',
    address_state: 'State',
    address_postal_code: 'Postal Code',
    address_country: 'Country',
    role: 'Role',
};

const steps = [
    { id: 'upload', label: 'Upload CSV' },
    { id: 'map', label: 'Map Fields' },
    { id: 'preview', label: 'Preview & Review' },
    { id: 'complete', label: 'Complete' },
];

const dialogRef = getDialogRef(ModalNames.UPLOAD_EMPLOYEES);
const businessStore = useBusinessStore();
const { showError } = useGlobalToast();

const currentStep = ref(1);
const isDragOver = ref(false);
const selectedFile = ref<File | null>(null);
// Why the dropped file was refused, shown under the drop zone until the next drop.
const fileError = ref('');
const fileInput = ref<HTMLInputElement | null>(null);
const parsedCsv = ref<{ headers: string[]; rows: Record<string, string>[] } | null>(null);
const columnMapping = ref<Record<string, string>>({});
const validatedRows = ref<Array<Record<string, unknown> & { _valid: boolean; _error?: string }>>([]);
const validCount = ref(0);
// Rows no batch got a verdict for. The preview says the import will still try them, so they are
// import-eligible even though the API called none of them valid.
const uncheckedCount = ref(0);
/**
 * Rows the preview can say are worth sending — the ones that passed and the ones no verdict came
 * back for. It gates the Import button and nothing else: the run SENDS every staged row, flagged
 * ones included, because the API checks each one again and its answer is the one that counts. The
 * note above the table therefore names the staged total, not this: saying "all 100 will be
 * imported" over a click that sent 120 hid that flagged rows were being retried at all.
 */
const importableCount = computed(() => validCount.value + uncheckedCount.value);

/** Rows not added because the run stopped: the refused batch, and everything after it. */
const stoppedCount = computed(() => importResult.value?.stopped_rows ?? 0);
/**
 * Rows the API rejected on their contents — `skipped` less the ones the run stopping cost us. The
 * two read the same to a member otherwise, and only this one is a reason to look at the file.
 */
const rejectedCount = computed(() => Math.max(0, (importResult.value?.skipped ?? 0) - stoppedCount.value));

/** Rows the import did not add: told so by the API, or never confirmed either way. */
const settledAgainst = computed(() => (importResult.value?.skipped ?? 0) + (importResult.value?.unconfirmed ?? 0));
// Four outcomes, not three. A run whose every batch timed out imported nothing we can
// count, but those batches carry no idempotency key and may well have committed — so the
// red cross and "Import Failed" told the member the one thing that makes re-uploading
// look safe. Unknown gets its own state, headed and coloured like the partial it may be.
const resultState = computed<'complete' | 'partial' | 'unconfirmed' | 'failed'>(() => {
    const result = importResult.value;
    if (!result) return 'failed';
    if (result.imported) return settledAgainst.value ? 'partial' : 'complete';
    if (result.unconfirmed) return 'unconfirmed';
    return 'failed';
});

/**
 * A raw CSV row in the canonical shape the import endpoint reads.
 *
 * The validate endpoint takes the mapping and answers in canonical keys; only the fallback for
 * a batch that never answered has to do it here, or `Given Name` reaches an endpoint expecting
 * `first_name`. An unmapped column is dropped, exactly as the server drops it.
 */
function canonicalise(row: Record<string, any>, mapping: Record<string, string>): Record<string, any> {
    const mapped: Record<string, any> = {};

    for (const [csvColumn, field] of Object.entries(mapping)) {
        if (field) mapped[field] = row[csvColumn];
    }

    return mapped;
}
const errorCount = ref(0);
/**
 * The rows the API actually judged and refused.
 *
 * `errorCount` is every row without a verdict, unchecked ones included — they are counted there so
 * the preview's totals add up to the file. Reported as "errors" that made an outage look like a bad
 * file, and contradicted the row-level promise that the import will still try them.
 */
const validationErrorCount = computed(() => Math.max(0, errorCount.value - uncheckedCount.value));
const isImporting = ref(false);
const isValidating = ref(false);
// How far through the file the current pass is, counted in rows rather than batches:
// the member cares about their 9,842 employees, not about our request size.
const batchProgress = ref<{ done: number; total: number } | null>(null);
const importResult = ref<BulkImportResult | null>(null);
const errorPage = ref(1);
const errorsPerPage = 5;

// Every row that did not import, paired with its original field data and the
// reason it failed — the single source for the on-screen list AND the error
// CSV. runImport submits the full validatedRows array in order, so the
// backend's 1-based error `row` indexes straight back into validatedRows.
const failedRows = computed(() => {
    const errs = importResult.value?.errors ?? [];
    return errs.map((e) => ({
        row: e.row,
        message: e.message,
        // A row whose batch never answered may have been created. It shares this list
        // because the member has to act on it, but calling it skipped — on screen or in
        // the report's `reason` column — asserts the one thing we do not know.
        unconfirmed: e.message === NOT_CONFIRMED,
        fields: (validatedRows.value[e.row - 1] ?? {}) as Record<string, unknown>,
    }));
});

// Every flagged row, plus the first PREVIEW_VALID_SHOWN valid ones, in file order
// and each carrying its file row number so a reason can be acted on.
const previewRows = computed(() => {
    const shown: Array<{ num: number; row: (typeof validatedRows.value)[number] }> = [];
    let validShown = 0;
    let flaggedShown = 0;
    validatedRows.value.forEach((row, i) => {
        if (row._valid) {
            if (validShown >= PREVIEW_VALID_SHOWN) return;
            validShown++;
        } else {
            if (flaggedShown >= PREVIEW_FLAGGED_SHOWN) return;
            flaggedShown++;
        }
        shown.push({ num: i + 1, row });
    });
    return shown;
});
/**
 * What the preview is ACTUALLY showing of each kind.
 *
 * The sentence above used to name the two ceilings, so a 250-row file with nothing wrong rendered
 * 50 valid rows and claimed 50 had a problem — contradicting its own zero-error summary two lines
 * up. A category with none shown is left out of the sentence rather than printed as a nought.
 */
const validRowsShown = computed(() => previewRows.value.filter((shown) => shown.row._valid).length);
const flaggedRowsShown = computed(() => previewRows.value.length - validRowsShown.value);

const previewHidden = computed(() => validatedRows.value.length - previewRows.value.length);
const flaggedRows = computed(() => validatedRows.value.filter((row) => !row._valid));
const flaggedHidden = computed(() => flaggedRows.value.length - Math.min(flaggedRows.value.length, PREVIEW_FLAGGED_SHOWN));

const totalErrorPages = computed(() => {
    const count = failedRows.value.length;
    return count ? Math.ceil(count / errorsPerPage) : 1;
});

const unconfirmedRows = computed(() => failedRows.value.filter((row) => row.unconfirmed));

const paginatedErrors = computed(() => {
    const start = (errorPage.value - 1) * errorsPerPage;
    return failedRows.value.slice(start, start + errorsPerPage);
});

const companyId = computed(() => dialogRef.value?.data?.companyId);
const companySlug = computed(() => dialogRef.value?.data?.companySlug);

// ── What each row will receive (D13, D14) ────────────────────────────────────
// Same reading of the setting as CreateEmployeeModal: Settings once loaded for
// THIS company, else the business payload for a user who may not read Settings.
const settingsLoaded = ref(false);
const seatCounts = ref<License[] | null>(null);
const autoProvision = computed(() => autoProvisionTier(settingsLoaded.value ? businessStore.settings : businessStore.business));
const autoProvisionSlug = computed(() => autoProvision.value ?? '');
const autoProvisionTierName = computed(() => {
    const slug = autoProvisionSlug.value;
    if (!slug) return '';
    return licenseTierName({ slug, name: businessStore.settings?.licenses?.find((l) => l.slug === slug)?.name });
});
const seatsFree = computed(() => seatCounts.value?.find((l) => l.slug === autoProvisionSlug.value)?.available ?? 0);
const rowCount = computed(() => parsedCsv.value?.rows.length ?? 0);
const licenseSentence = computed(() => {
    if (autoProvision.value === null) return '';
    const tier = autoProvisionTierName.value;
    if (!tier) return 'Auto-Provision is off: employees are added without a license.';
    // Channel-partner companies provision on demand, so no seat count applies.
    if (businessStore.business?.is_partner) return `Each employee will receive a ${tier} license.`;
    if (seatCounts.value === null) return `Each employee will receive a ${tier} license if one is free.`;
    const free = seatsFree.value;
    if (rowCount.value > free) {
        return free > 0
            ? `Each employee will receive a ${tier} license, but only ${free} ${free === 1 ? 'license is' : 'licenses are'} free: the rest will be added without a license.`
            : `Auto-Provision is on, but no ${tier} licenses are free: employees will be added without a license.`;
    }
    return `Each employee will receive a ${tier} license (${free} free).`;
});

// ── Done screen (D14, D21) ───────────────────────────────────────────────────
const unlicensed = computed(() => importResult.value?.without_license ?? []);
// 'no_seats' is only reported when Auto-Provision actually tried, so Buy seats
// never appears next to a file imported with it off.
const seatsRanOut = computed(() => unlicensed.value.some((p) => p.reason === 'no_seats'));
const unlicensedReason = computed(() => {
    if (!unlicensed.value.length) return '';
    const tier = autoProvisionTierName.value;
    if (seatsRanOut.value) return `no ${tier ? `${tier} ` : ''}licenses were free`;
    if (unlicensed.value.every((p) => p.reason === 'auto_provision_off')) return 'Auto-Provision is off';
    return "the Auto-Provision license couldn't be assigned";
});
const canBuySeats = computed(() => businessStore.hasPermission('financial', 'write'));

// Whose welcome email was not handed to the mail system, when the API names them (D21).
const emailsFailed = computed(() => importResult.value?.emails_failed ?? []);
const resend = reactive<Record<number, 'sending' | 'sent' | 'failed'>>({});
async function resendInvite(employeeId: number) {
    if (!companyId.value || resend[employeeId] === 'sending') return;
    resend[employeeId] = 'sending';
    try {
        await businessService.sendEmployeeInvite(companyId.value, employeeId);
        resend[employeeId] = 'sent';
    } catch {
        resend[employeeId] = 'failed';
    }
}

// Seats are bought in the checkout app, a page rather than a dialog — in a NEW TAB,
// so this done screen (the only record of who has no seat) stays on screen.
//
// These rows went unlicensed because Auto-Provision ran out, so the tier to buy is the
// Auto-Provision tier: naming it lands the admin on that plan's summary instead of the
// chooser. Empty when settings never loaded, which just opens the chooser as before.
function openPurchaseLicenses() {
    if (!canBuySeats.value || !companyId.value) return;
    if (isDemoMode()) {
        showError('Not in the demo', 'Buying licenses opens the real checkout, which the demo cannot reach.');
        return;
    }
    const url = checkoutBusinessUrl(companyId.value, `/business/${companySlug.value ?? ''}/employees`, {
        plan: autoProvisionSlug.value,
    });
    window.open(url, '_blank', 'noopener');
}

onMounted(async () => {
    try {
        await businessStore.fetchSettings();
        settingsLoaded.value = true;
    } catch (error) {
        console.warn('UploadEmployeesModal: settings unavailable for this user.', error);
    }
    if (!companyId.value) return;
    try {
        seatCounts.value = await businessService.getLicenseInformation(companyId.value);
    } catch (error) {
        console.error('Failed to load license counts:', error);
    }
});

const csvHeaders = computed(() => parsedCsv.value?.headers ?? []);

const fieldOptions = computed(() => [
    { label: '— Select field —', value: '' },
    ...ALL_FIELDS.map((f) => ({ label: FIELD_LABELS[f] || f, value: f })),
]);

const missingRequired = computed(() => {
    const mapped = new Set(Object.values(columnMapping.value).filter(Boolean));
    return REQUIRED_FIELDS.filter((f) => !mapped.has(f));
});
const allRequiredMapped = computed(() => Boolean(parsedCsv.value) && missingRequired.value.length === 0);

function closeDialog() {
    // An unconfirmed batch counts. Those rows may have been committed, and the member has just
    // been told to check whether they were — sending them back to a roster this modal declined
    // to refresh is the one thing that makes that check impossible.
    const uploaded = Boolean(importResult.value && (importResult.value.imported > 0 || importResult.value.unconfirmed));
    dialogRef.value.close({ uploaded });
}

function openFilePicker() {
    fileInput.value?.click();
}

function handleFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files?.length) {
        processFile(target.files[0]);
    }
}

function handleDragOver() {
    isDragOver.value = true;
}

function handleDragLeave() {
    isDragOver.value = false;
}

function handleDrop(event: DragEvent) {
    isDragOver.value = false;
    if (event.dataTransfer?.files?.length) {
        processFile(event.dataTransfer.files[0]);
    }
}

// Monotonic guard for the asynchronous CSV read. Each newly accepted file bumps the
// token; a FileReader that resolves after its file was replaced sees a stale token and
// drops its result, so a slow read of file A can't overwrite the parsed rows/mapping of
// file B the user selected next.
let fileReadToken = 0;

// Monotonic guard for Validate & Preview: mapping edits, Back, and file
// replacement/removal all bump it, so a slower validation of superseded
// input can't advance the modal and stage stale rows for Import.
let validateToken = 0;

function processFile(file: File) {
    // Clear the prior file's state before validating the replacement: an invalid
    // drop returns early below, and leaving the old file/rows/mapping intact would
    // keep it shown as accepted with Next/Import enabled behind the new file's name.
    const token = ++fileReadToken;
    ++validateToken;
    selectedFile.value = null;
    parsedCsv.value = null;
    columnMapping.value = {};
    fileError.value = '';
    fileInput.value && (fileInput.value.value = '');
    if (!file.name.toLowerCase().endsWith('.csv')) {
        fileError.value = 'Choose a .csv file.';
        return;
    }
    if (file.size > MAX_FILE_BYTES) {
        fileError.value = `File size must be less than ${formatBytes(MAX_FILE_BYTES)}.`;
        return;
    }
    selectedFile.value = file;
    parseCsv(file, token);
}

async function parseCsv(file: File, token: number) {
    let text: string;
    try {
        // Strict UTF-8 (D33): an Excel "CSV" in Windows-1252 would otherwise save
        // Świątek as garbage, silently. The decoder also drops a leading byte-order mark.
        text = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
    } catch {
        if (token !== fileReadToken) return;
        fileError.value = 'Save the file as UTF-8 and upload it again.';
        return;
    }
    // Drop a superseded read: if the user replaced this file before its bytes
    // finished loading, applying its parsed rows/mapping (or surfacing its parse
    // errors) would describe a file that is no longer selected.
    if (token !== fileReadToken) return;
    const records = parseCsvText(text);
    const headers = records[0] ?? [];
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < records.length; i++) {
        const values = records[i];
        // Skip empty rows (file ending in a trailing newline, etc.).
        if (values.length === 1 && values[0] === '') continue;
        const row: Record<string, string> = {};
        headers.forEach((h, j) => {
            row[h] = values[j] ?? '';
        });
        rows.push(row);
    }
    // A header with only blank/trailing rows leaves nothing to import —
    // surface it here rather than advancing and POSTing rows:[] (generic 422).
    if (rows.length === 0) {
        fileError.value = 'Add at least one employee row.';
        return;
    }
    if (rows.length > MAX_ROWS) {
        fileError.value = `This file has ${formatNumber(rows.length)} employees. Upload at most ${formatNumber(MAX_ROWS)} at a time.`;
        return;
    }
    parsedCsv.value = { headers, rows };
    autoMatchColumns(headers);
}

// RFC 4180 tokenizer: treats `""` as an escaped literal quote inside a
// quoted field, and allows newlines inside quoted fields without splitting
// records. The previous split-by-newline + per-line `inQuotes` toggle
// broke valid CSV (escaped quotes shifted columns; multiline cells from
// spreadsheet exports were treated as multiple rows).
function parseCsvText(text: string): string[][] {
    const records: string[][] = [];
    let field = '';
    let row: string[] = [];
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += c;
            }
            continue;
        }
        if (c === '"') {
            inQuotes = true;
        } else if (c === ',') {
            row.push(field);
            field = '';
        } else if (c === '\n' || c === '\r') {
            // Treat \r, \n, and \r\n as a single record terminator.
            if (c === '\r' && text[i + 1] === '\n') i++;
            row.push(field);
            records.push(row);
            row = [];
            field = '';
        } else {
            field += c;
        }
    }
    // Flush the trailing field/row (when the file doesn't end with a newline).
    if (field !== '' || row.length > 0) {
        row.push(field);
        records.push(row);
    }
    return records.map((r) => r.map((v) => v.trim()));
}

function autoMatchColumns(headers: string[]) {
    const mapping: Record<string, string> = {};
    const aliases: Record<string, string[]> = {
        first_name: ['first name', 'firstname', 'first', 'given name'],
        last_name: ['last name', 'lastname', 'last', 'surname', 'family name'],
        email: ['email', 'email address', 'e-mail', 'mail'],
        department: ['department', 'dept'],
        phone: ['phone', 'telephone', 'mobile', 'cell', 'phone number'],
        dob: ['dob', 'date of birth', 'birthday', 'birth date', 'birthdate'],
        address_line_1: ['address', 'address line 1', 'street', 'street address', 'address_line_1'],
        address_city: ['city', 'address city', 'address_city'],
        address_state: ['state', 'region', 'province', 'address state', 'address_state'],
        address_postal_code: ['postal code', 'zip', 'zip code', 'zipcode', 'postal', 'address_postal_code'],
        address_country: ['country', 'address country', 'address_country'],
        role: ['role', 'roles'],
    };
    headers.forEach((h) => {
        const lower = h.toLowerCase().trim();
        const normalized = lower.replace(/\s+/g, '_');

        // 1. Direct match to field name (e.g. "address_city" → address_city)
        if (ALL_FIELDS.includes(normalized)) {
            mapping[h] = normalized;
            return;
        }

        // 2. Exact alias match first (prevents "address" substring matching all address_* fields)
        for (const [field, names] of Object.entries(aliases)) {
            if (names.some((n) => lower === n)) {
                mapping[h] = field;
                return;
            }
        }

        // 3. Substring match as fallback (but skip short generic terms like "address")
        const ambiguous = ['address'];
        for (const [field, names] of Object.entries(aliases)) {
            if (names.some((n) => !ambiguous.includes(n) && lower.includes(n))) {
                mapping[h] = field;
                return;
            }
        }
    });
    columnMapping.value = mapping;
}

function setMapping(csvCol: string, value: string) {
    // A mapping edit supersedes any validation still awaiting on the old mapping.
    ++validateToken;
    columnMapping.value = { ...columnMapping.value, [csvCol]: value };
}

function getMappingStatusClass(csvCol: string): string {
    const v = columnMapping.value[csvCol];
    if (!v) return 'pending';
    return REQUIRED_FIELDS.includes(v) ? 'matched' : 'optional';
}

function getMappingStatusLabel(csvCol: string): string {
    const v = columnMapping.value[csvCol];
    if (!v) return 'Select Field';
    return 'Auto-matched';
}

function removeFile() {
    // Invalidate any in-flight read the same way processFile() does when
    // replacing a file, so a late FileReader.onload can't repopulate parsedCsv
    // for a file the user just removed.
    ++fileReadToken;
    ++validateToken;
    selectedFile.value = null;
    parsedCsv.value = null;
    columnMapping.value = {};
    fileError.value = '';
    fileInput.value && (fileInput.value.value = '');
}

async function downloadTemplate() {
    if (!companyId.value) return;
    try {
        await businessService.downloadBulkUploadTemplate(companyId.value);
    } catch (e: any) {
        showError('Download Failed', e.message || 'Failed to download template');
    }
}

function goBack() {
    if (currentStep.value === 2) {
        // Going back to step 1: reset parsed state so user can re-upload.
        // Bump the token too so a validation still awaiting for this file
        // can't advance the modal after the file state is gone.
        ++validateToken;
        selectedFile.value = null;
        parsedCsv.value = null;
        columnMapping.value = {};
        fileInput.value && (fileInput.value.value = '');
    }
    currentStep.value--;
}

function goToMapFields() {
    currentStep.value = 2;
}

async function goToPreview() {
    if (!companyId.value || !parsedCsv.value) return;
    const token = ++validateToken;
    const mapping: Record<string, string> = {};
    Object.entries(columnMapping.value).forEach(([k, v]) => {
        if (v) mapping[k] = v;
    });
    const rows = parsedCsv.value.rows;
    const merged: typeof validatedRows.value = [];
    let valid = 0;
    let errors = 0;
    let failedBatches = 0;
    let batches = 0;
    let lastError = '';
    let unchecked = 0;
    let consecutiveFailures = 0;
    // The refusal that ends the whole pass — throttled, or an authorization failure that will
    // answer every batch the same way. Held rather than flagged, so the member is told which.
    let runRefused: ApiError | null = null;
    isValidating.value = true;
    batchProgress.value = { done: 0, total: rows.length };
    try {
        // A batch at a time, in order. row_offset tells the API where the batch sat,
        // so the verdicts come back numbered from the file and the merged array lines
        // up with the rows the member can see in their spreadsheet.
        for (let start = 0; start < rows.length; start += BATCH_SIZE) {
            const batch = rows.slice(start, start + BATCH_SIZE);
            batches++;
            let result: { rows: typeof merged; valid_count: number; error_count: number };
            try {
                result = await businessService.validateBulkUploadRows(companyId.value, batch, mapping, start);
                consecutiveFailures = 0;
            } catch (e: any) {
                // The service answering "no" is not a batch that failed to answer. Letting it
                // fall through marked the remainder unchecked and showed a preview offering to
                // import it — and Import then sent the first batch straight back into the same
                // refusal, where the run-level handling called the whole file skipped without a
                // row of it having been looked at.
                //
                // The same predicate the import loop uses, so the two cannot disagree about what
                // is worth another ninety-nine requests: an expired session, a withdrawn grant, a
                // throttle, a company that is gone, a route that moved.
                if (stopsTheRun(e)) {
                    runRefused = e as ApiError;
                    break;
                }

                // One batch that never answered must not throw away the ninety-nine
                // already checked. These rows carry no verdict; the import validates
                // every row it is given, so they still get a real answer there.
                failedBatches++;
                consecutiveFailures++;
                lastError = e?.message || '';
                // MAPPED, not raw. A successful batch comes back in the canonical shape the
                // import expects; copying the CSV's own column names here sent `Given Name`
                // to an endpoint that reads `first_name`, so rows the preview promised would
                // still be tried were rejected for the shape of the fallback rather than for
                // anything in them.
                result = {
                    rows: batch.map((row) => ({
                        ...canonicalise(row, mapping),
                        _valid: false,
                        _error: "Couldn't be checked; the import will still try it.",
                    })),
                    valid_count: 0,
                    error_count: batch.length,
                };
                // Counted so the Import button can offer them. `valid_count` is the API's
                // verdict and these have none, but the copy promises the import will try them —
                // and with every answering batch invalid, a zero `validCount` made that promise
                // unkeepable.
                unchecked += batch.length;
            }
            // Drop a superseded validation: the user changed the mapping, went
            // back, or replaced the file while this was awaiting — applying it
            // would let Import submit rows for data that no longer exists.
            if (token !== validateToken) return;
            merged.push(...result.rows);
            valid += result.valid_count;
            errors += result.error_count;
            batchProgress.value = { done: merged.length, total: rows.length };

            // Two in a row is the API being down, not this slice of the file — the same rule the
            // import follows. A hundred batches each waiting out its own timeout holds the modal
            // for tens of minutes to learn what the second failure already said, so the rest go
            // through unchecked and the import validates them.
            if (consecutiveFailures >= 2) {
                const remaining = rows.slice(start + batch.length);

                for (const row of remaining) {
                    merged.push({
                        ...canonicalise(row, mapping),
                        _valid: false,
                        _error: "Couldn't be checked; the import will still try it.",
                    });
                }

                unchecked += remaining.length;
                errors += remaining.length;
                batchProgress.value = { done: merged.length, total: rows.length };
                break;
            }
        }
    } finally {
        // Only one pass is ever in flight — Next is disabled while it runs — so clear
        // these whatever the token says. A mapping edit mid-pass bumps the token, and
        // leaving the flags set for its sake would strand the modal with Next disabled.
        isValidating.value = false;
        batchProgress.value = null;
    }

    // The pass was refused outright: the rows that did not get through are unchecked, and the
    // import would send its first batch into the same refusal. Hold on the mapping step with a
    // reason the member can act on, rather than offering a preview that invites exactly that run.
    if (runRefused !== null) {
        if (runRefused.code === 429) {
            showError('Too Many Requests', 'The service is busy. Wait a moment and check the file again.');
        } else if (runRefused.code === 401 || runRefused.code === 403) {
            showError('Not Allowed', memberFacingMessage(runRefused, 'Your session may have ended. Sign in and try again.'));
        } else {
            // A refusal that is about neither the rows nor this member's access — a company that
            // is gone, a route that moved. Named as the file's innocence, because the member's
            // next move is to try later rather than to edit a spreadsheet.
            showError('Check Failed', memberFacingMessage(runRefused, 'The check was refused for the whole file, not for a row in it. Try again shortly.'));
        }

        return;
    }

    // Not one batch got through: the API is unreachable, which is not the file's
    // fault. Say so and stay on the mapping step rather than showing a preview in
    // which every row is a question mark.
    if (failedBatches === batches) {
        showError('Validation Failed', lastError || 'Failed to validate rows');
        return;
    }

    // The API only sees one batch at a time, so it can only call a repeat a duplicate when both
    // copies share a batch; a second copy a batch away would pass here and fail at import. Same
    // rule across the whole file: a row claims its address once it is otherwise valid, and a later
    // valid copy is flagged against the first, as the API words it.
    const claimed = new Map<string, number>();
    merged.forEach((row, i) => {
        if (!row._valid) return;
        const email = String(row.email ?? '').trim().toLowerCase();
        // No address to claim, no duplicate: a row the API called valid but answered without an
        // `email` key leaves this empty, and letting '' be an address made every such row a
        // duplicate of the first — Import off for a file whose rows all differ.
        if (!email) return;
        const first = claimed.get(email);
        if (first === undefined) {
            claimed.set(email, i + 1);
            return;
        }
        row._valid = false;
        row._error = `Duplicate of row ${first}`;
        valid--;
        errors++;
    });

    validatedRows.value = merged;
    validCount.value = valid;
    errorCount.value = errors;
    uncheckedCount.value = unchecked;
    currentStep.value = 3;
}

async function runImport() {
    // Unchecked rows count: the preview says the import will still try them, and a file whose
    // every ANSWERING batch was invalid otherwise left that promise unkeepable — the button
    // disabled and this guard refusing, with rows nobody had judged still in the list.
    if (!companyId.value || importableCount.value === 0) return;
    // Submit every row in preview order — the backend is the single validator:
    // it creates the valid rows and reports each skip with a reason, so the
    // result screen accounts for both preview-rejected and import-time failures.
    const rows = validatedRows.value.map(({ _valid, _error, ...r }) => r);
    isImporting.value = true;
    batchProgress.value = { done: 0, total: rows.length };

    // One reconciled result for the whole file. A count the API never answered with
    // stays absent, because the done screen tells that apart from zero: an older API
    // shows "Employees added", not "Added with a license: 0".
    const merged: BulkImportResult = { imported: 0, skipped: 0, errors: [] };
    const add = (a: number | undefined, b: number | undefined) => (b === undefined ? a : (a ?? 0) + b);
    // Name a stretch of rows by file row number, so they appear in the result and in the
    // downloadable report like any other skip.
    //
    // Two different stretches, kept apart. A batch that was SENT and never answered may have
    // committed — this POST has no idempotency key, so "upload these again" is how the same
    // people get created twice — while rows the run never reached were definitely not created
    // and can be re-uploaded safely.
    const markUnconfirmed = (from: number, upTo: number) => {
        merged.unconfirmed = (merged.unconfirmed ?? 0) + (upTo - from);
        for (let i = from; i < upTo; i++) merged.errors.push({ row: i + 1, message: NOT_CONFIRMED });
    };
    const markNeverSent = (from: number, upTo: number, message: string = NOT_IMPORTED) => {
        merged.skipped += upTo - from;
        markStopped(upTo - from);
        for (let i = from; i < upTo; i++) merged.errors.push({ row: i + 1, message });
    };
    /** Why the run stopped, in words the member can act on rather than a row-level retry. */
    const runStopMessage = (e: ApiError): string => {
        if (e.code === 429) return STOPPED_BUSY;
        // The API's own reason where it gave one, so a specific refusal is not flattened into
        // this general sentence ({@see memberFacingMessage}).
        if (e.code === 401 || e.code === 403) return memberFacingMessage(e, STOPPED_ACCESS);

        return memberFacingMessage(e, STOPPED_REFUSED);
    };
    /**
     * Rows the run stopping cost us, not the file. Inside `skipped` so the totals balance, counted
     * again here so the summary can tell them from a row the API rejected on its contents.
     */
    const markStopped = (count: number) => {
        merged.stopped_rows = (merged.stopped_rows ?? 0) + count;
    };

    let failures = 0;
    for (let start = 0; start < rows.length; start += BATCH_SIZE) {
        const batch = rows.slice(start, start + BATCH_SIZE);
        try {
            const result = await businessService.importBulkUploadRows(companyId.value, batch, start);
            failures = 0;
            merged.imported += result.imported ?? 0;
            merged.skipped += result.skipped ?? 0;
            merged.errors.push(...(result.errors ?? []));
            merged.imported_with_license = add(merged.imported_with_license, result.imported_with_license);
            merged.imported_without_license = add(merged.imported_without_license, result.imported_without_license);
            merged.emails_queued = add(merged.emails_queued, result.emails_queued);
            if (result.without_license?.length) merged.without_license = [...(merged.without_license ?? []), ...result.without_license];
            if (result.emails_failed?.length) merged.emails_failed = [...(merged.emails_failed ?? []), ...result.emails_failed];
        } catch (e: any) {
            // A batch that failed must not take the rest of the file with it: the
            // batches before it are committed and cannot be undone, so the only
            // honest thing is to keep going and account for this one.
            //
            // WHICH kind of failure, though. A 4xx is the server answering — it read the
            // request and refused it, so nothing was written and the rows are a real skip
            // carrying the API's own reason. Only a 5xx or a transport failure leaves the
            // outcome unknown. Counting a 401 as "may have committed" both hid an actionable
            // error and told the member their rows might be duplicates.
            const refused = e instanceof ApiError && e.code >= 400 && e.code < 500;

            if (refused) {
                merged.skipped += batch.length;
                // Does this answer the rows or the request? An expired session, a withdrawn
                // grant, a throttle, a route that is gone — every one of those refuses the next
                // ninety-nine batches exactly as it refused this one, and ninety-nine more
                // requests teach the member nothing. Honouring Retry-After on a 429 would mean
                // holding the modal open for however long it says; stopping names the rest as
                // never sent, which is true and leaves them safe to upload again in a minute.
                const runStopped = stopsTheRun(e);
                const reason = runStopped ? runStopMessage(e) : memberFacingMessage(e, NOT_IMPORTED);
                for (let i = start; i < start + batch.length; i++) merged.errors.push({ row: i + 1, message: reason });

                if (runStopped) {
                    // This batch too: it reached the API and was turned away for the run's sake, so
                    // nothing in it was judged on its contents either. The rows after it carry the
                    // same sentence, because the same refusal is what they are waiting behind.
                    markStopped(batch.length);
                    markNeverSent(start + batch.length, rows.length, reason);
                    break;
                }

                failures = 0;
                batchProgress.value = { done: Math.min(start + batch.length, rows.length), total: rows.length };
                continue;
            }

            markUnconfirmed(start, start + batch.length);
            // Two in a row is the API being down rather than this slice of the file.
            // Name the rest and stop; ninety more requests that also time out leave
            // the member staring at a spinner for an hour to learn the same thing.
            if (++failures >= 2) {
                // Never sent, so never created: these are safe to upload again.
                markNeverSent(start + batch.length, rows.length);
                break;
            }
        }
        batchProgress.value = { done: Math.min(start + batch.length, rows.length), total: rows.length };
    }

    importResult.value = merged;
    errorPage.value = 1;
    currentStep.value = 4;
    isImporting.value = false;
    batchProgress.value = null;
}

function csvEscape(value: string): string {
    return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

// The report: canonical field columns (so a fixed file auto-maps on the next
// upload) plus a `reason` column, one row per skipped row and one per person
// added without a license (D14) — the admin's record of who still needs a seat.
function csvLines(): { lines: string[]; line: (fields: Record<string, unknown>, reason: string) => string } {
    const header = [...ALL_FIELDS, 'reason'];
    const line = (fields: Record<string, unknown>, reason: string) =>
        [...ALL_FIELDS.map((f) => csvEscape(String(fields?.[f] ?? ''))), csvEscape(reason)].join(',');

    return { lines: [header.map(csvEscape).join(',')], line };
}

function downloadCsv(contents: string, filename: string) {
    const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}

// Every flagged row from the preview, in file order, with the reason the table would have
// shown — the whole list, however much of it the bounded table drew.
function downloadFlaggedCsv() {
    if (!flaggedRows.value.length) return;
    const { lines, line } = csvLines();
    validatedRows.value.forEach((row, i) => {
        if (row._valid) return;
        lines.push(line(row as Record<string, unknown>, `Row ${i + 1}: ${row._error ?? 'Not valid'}`));
    });
    downloadCsv(lines.join('\r\n'), 'employee_bulk_upload_flagged_rows.csv');
}

function reportCsv(): string {
    const { lines, line } = csvLines();
    for (const { fields, message, unconfirmed } of failedRows.value) {
        lines.push(line(fields, `${unconfirmed ? 'Not confirmed' : 'Skipped'}: ${message}`));
    }
    for (const person of unlicensed.value) {
        lines.push(line((validatedRows.value[person.row - 1] ?? {}) as Record<string, unknown>, `Added without a license: ${unlicensedReason.value}`));
    }
    return lines.join('\r\n');
}

function downloadReportCsv() {
    if (!failedRows.value.length && !unlicensed.value.length) return;
    downloadCsv(reportCsv(), 'employee_bulk_upload_report.csv');
}

</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-container {
    @apply flex flex-col;
    max-height: 85vh;
}

.stepper {
    @apply flex items-center gap-0 px-8 pb-6 flex-shrink-0;
}

.stepper-item {
    @apply flex items-center gap-2;

    .stepper-circle {
        @apply w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors;
        @apply bg-white border-zinc-200 text-zinc-500;

        .ph {
            @apply text-white;
        }
    }

    .stepper-item.active .stepper-circle {
        @apply border-blue-600 bg-blue-600 text-white;
    }

    .stepper-item.completed .stepper-circle {
        @apply border-green-500 bg-green-500 text-white;
    }

    .stepper-label {
        @apply text-sm font-medium text-zinc-500 whitespace-nowrap;
    }

    .stepper-item.active .stepper-label {
        @apply text-blue-600 font-semibold;
    }

    .stepper-item.completed .stepper-label {
        @apply text-zinc-700;
    }

    .stepper-line {
        @apply flex-1 h-0 mx-3 min-w-[20px] border-t-2 border-dashed border-zinc-200 self-center;

        &.completed {
            @apply border-green-500 border-solid;
        }
    }
}

.modal-body {
    // The one wizard whose step content scrolls rather than the page: it takes the
    // card's remaining height and reserves a taller floor than the shared wizard body,
    // because the mapping and preview steps are tall enough that a shorter one jumps.
    //
    // The floor exists only where it fits. The card is capped at 85vh and clips its overflow, and
    // header + stepper + footer take roughly 205px of it, so a floor of any size (a flat 320px, or a
    // fraction of the viewport height) pushes the footer (Back / Next / Import / Done) out of sight once 85vh is
    // less than 205px + the floor. Below 640px of viewport height (85vh = 544px, room for 320px
    // with margin) the body has no floor, shrinks to whatever is left and scrolls. Chrome alone
    // exceeds the cap under about 240px, which no body rule can fix.
    @apply overflow-y-auto flex-1;
    min-height: 0;

    @media (min-height: 640px) {
        min-height: 320px;
    }
}

.modal-header,
.modal-footer {
    @apply flex-shrink-0;
}

.step-content {
    @apply py-4;
}

.drop-zone {
    @apply border-2 border-dashed border-zinc-300 rounded-xl p-12 transition-colors cursor-pointer min-h-[180px] flex items-center justify-center;

    &:hover, &.drag-over {
        @apply border-zinc-400 bg-zinc-50;
    }

    .drop-zone-content {
        @apply flex flex-col items-center gap-3 text-center;

        p {
            @apply text-base font-medium text-zinc-700 m-0;
        }

        .browse-link {
            @apply text-blue-600 underline cursor-pointer font-medium;
        }
    }

    .selected-file {
        @apply flex items-center gap-3 p-3 border border-green-200 bg-green-50 rounded-lg;

        .file-name {
            @apply font-medium text-zinc-900;
        }

        .file-size {
            @apply text-sm text-zinc-500 ml-2;
        }

        .remove-file {
            @apply ml-auto w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50;
        }
    }
}

.field-error {
    @apply text-sm text-red-600 mt-3 m-0;
}

.license-hint {
    @apply text-sm text-zinc-500 mt-4;
}

.template-link {
    @apply text-sm text-zinc-600 flex items-center gap-2;

    a {
        @apply text-blue-600 hover:underline font-medium;
    }
}

.column-chips {
    .chip-label {
        @apply text-sm font-semibold text-zinc-600 mb-2;
    }

    .chips {
        @apply flex flex-wrap gap-2;
    }

    .chip {
        @apply px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 text-sm font-medium;
    }
}

.map-instruction {
    @apply text-sm text-zinc-600;
}

.mapping-table {
    @apply border border-zinc-200 rounded-lg overflow-hidden;

    .mapping-header {
        @apply grid grid-cols-[1fr_1fr_120px] gap-4 px-4 py-3 bg-zinc-50 text-sm font-semibold text-zinc-700;
    }

    .mapping-row {
        @apply grid grid-cols-[1fr_1fr_120px] gap-4 px-4 py-3 border-t border-zinc-100 items-center;
    }

    .csv-col-input {
        @apply bg-zinc-50;
    }

    .status-chip {
        @apply inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full;

        &.matched {
            @apply bg-green-100 text-green-700;
        }

        &.pending {
            @apply bg-amber-100 text-amber-700;
        }

        &.optional {
            @apply bg-green-100 text-green-700;
        }
    }
}

.preview-summary {
    @apply flex gap-4;

    .summary-valid {
        @apply flex items-center gap-1.5 text-sm text-green-600 font-medium;
    }

    .summary-errors {
        @apply flex items-center gap-1.5 text-sm text-red-600 font-medium;
    }

    // Amber, not red: these rows were not refused, and the import will still try them.
    .summary-unchecked {
        @apply flex items-center gap-1.5 text-sm text-amber-600 font-medium;
    }
}

.preview-hidden {
    @apply text-sm text-zinc-500 mt-3 m-0;
}

.batch-progress {
    @apply text-sm text-zinc-500 mt-4 m-0;
}

.preview-row-num {
    @apply text-zinc-400 tabular-nums;
}

.preview-table-wrapper {
    @apply border border-zinc-200 rounded-lg max-h-[240px];
    // Always-visible scrollbars so it's obvious the grid scrolls. The table is
    // always wider than the modal (min-w below), so force the horizontal bar on;
    // the vertical bar shows when rows overflow. Styling the webkit scrollbar
    // also opts out of macOS overlay auto-hide, keeping the bar persistent.
    overflow-x: scroll;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--hairline-strong) var(--surface-sunken);

    &::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }

    &::-webkit-scrollbar-track {
        @apply bg-zinc-100;
    }

    &::-webkit-scrollbar-thumb {
        @apply bg-zinc-300 rounded-full;
        border: 2px solid var(--surface-sunken);

        &:hover {
            @apply bg-zinc-400;
        }
    }
}

.preview-table {
    @apply w-full text-sm min-w-[680px];

    th {
        @apply px-4 py-3 text-left font-semibold text-zinc-700 bg-zinc-50 whitespace-nowrap;
    }

    td {
        @apply px-4 py-2 border-t border-zinc-100 whitespace-nowrap;
    }

    .row-error {
        @apply bg-red-50;
    }

    .status-chip.valid {
        @apply bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs;
    }

    .status-chip.error {
        @apply bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs;
    }
}

.complete-step {
    @apply text-center;

    .complete-icon {
        @apply mb-4 flex justify-center;
    }

    .complete-title {
        @apply text-xl font-semibold text-zinc-900 m-0 mb-2;
    }

    .complete-message {
        @apply text-zinc-600 mb-4;
    }

    .complete-emails {
        @apply text-sm text-zinc-600 mb-4;
    }

    .report-actions {
        @apply flex justify-end mb-2;
    }

    .unlicensed-list,
    .emails-failed-list {
        @apply text-left mb-4 bg-amber-50 rounded-lg p-3;

        .error-list-title {
            @apply text-sm font-semibold text-zinc-700 m-0 mb-1;
        }

        .inline-link {
            @apply text-sm font-medium text-blue-600 hover:underline ml-1;
        }

        ul {
            @apply m-0 p-0 list-none;
        }

        li {
            @apply flex justify-between gap-3 py-1 text-sm border-b border-amber-100 last:border-0;
        }
    }

    .complete-summary {
        @apply bg-zinc-50 rounded-lg p-4 mb-4 text-left max-w-xs mx-auto;

        .summary-row {
            @apply flex justify-between py-1 text-sm;
        }
    }

    .error-list {
        @apply text-left mb-4;

        .error-list-header {
            @apply flex justify-between items-center gap-3 mb-2;
        }

        .error-list-title {
            @apply text-sm font-semibold text-zinc-700 m-0;
        }

        .error-list-count {
            @apply text-xs text-zinc-400;
        }

        .error-list-items {
            @apply bg-red-50 rounded-lg p-3;
        }

        .error-table-header {
            @apply flex gap-3 pb-1.5 mb-1 border-b border-red-200 text-xs font-semibold text-red-400 uppercase tracking-wide;

            span:first-child {
                @apply w-12 flex-shrink-0;
            }
        }

        .error-item {
            @apply flex gap-3 py-1.5 text-sm border-b border-red-100 last:border-0;
        }

        .error-row {
            @apply text-red-600 font-medium whitespace-nowrap w-12 flex-shrink-0;
        }

        .error-msg {
            @apply text-red-700;
        }

        // Amber, not red: this row was not refused, and the list it shares is red.
        .status-chip.unconfirmed {
            @apply bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs mr-1;
        }
    }

    .error-pagination {
        @apply flex items-center justify-between mt-2 text-xs text-zinc-500;

        button {
            @apply px-2 py-1 rounded border border-zinc-200 bg-white cursor-pointer text-zinc-600;

            &:hover:not(:disabled) {
                @apply bg-zinc-50;
            }

            &:disabled {
                @apply opacity-30 cursor-not-allowed;
            }
        }
    }

    .error-download-btn {
        @apply inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 cursor-pointer bg-transparent border-0 p-0;
        @apply hover:text-blue-700 hover:underline;
    }

    .info-alert {
        @apply flex items-start gap-3 p-4 bg-blue-50 rounded-lg text-sm text-blue-700;
    }
}

.hidden {
    @apply absolute w-0 h-0 opacity-0 pointer-events-none;
}
</style>
